// src/pages/Market.js

import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import {
  RefreshCw,
  TrendingUp,
  Coins,
  Gem,
  Server,
  ArrowPathIcon,
  Blocks,
  ArrowLeft,
} from "lucide-react";

import BaitLogo from "../BaitLogo.png";

import "./Market.css";
import "../components/Walletview.css";

// =========================================================
// APIs
// =========================================================

const GOLD_API =
  "https://goldapi.bait-coin.com/api/gold-reference";

const GOLDCHAIN_RPC =
  "https://rpc.goldchain.tech";

// =========================================================
// CHAIN CONFIGURATION
// =========================================================

const GENESIS_SUPPLY = 100;
const BLOCK_REWARD = 2;
const CHAIN_ID = 777771;

// =========================================================
// FIXED WALLET PAGE SIZE
// =========================================================

const PAGE_WIDTH = 450;
const PAGE_HEIGHT = 750;

// =========================================================
// RPC HELPER
// =========================================================

const rpcCall = async (
  method,
  params = []
) => {
  const response = await fetch(
    GOLDCHAIN_RPC,
    {
      method: "POST",

      headers: {
        "Content-Type": "application/json",
      },

      body: JSON.stringify({
        jsonrpc: "2.0",
        id: Date.now(),
        method,
        params,
      }),
    }
  );

  if (!response.ok) {
    throw new Error(
      `RPC request failed: ${response.status}`
    );
  }

  const json = await response.json();

  if (json.error) {
    throw new Error(
      json.error.message ||
        "RPC error"
    );
  }

  return json.result;
};

// =========================================================
// HEX TO NUMBER
// =========================================================

const hexToNumber = (hex) => {
  if (
    hex === undefined ||
    hex === null ||
    hex === ""
  ) {
    return 0;
  }

  const value = parseInt(hex, 16);

  return Number.isFinite(value)
    ? value
    : 0;
};

// =========================================================
// MINI MARKET GRAPH
// =========================================================

const MiniMarketGraph = ({
  value,
  variant = "default",
}) => {
  const numericValue =
    Number(value) || 1;

  const variations = [
    0.91,
    0.94,
    0.92,
    0.97,
    0.95,
    1.01,
    0.98,
    1.04,
    1.02,
    1.08,
    1.05,
    1.12,
  ];

  const points = variations
    .map((multiplier, index) => {
      const x =
        (index /
          (variations.length - 1)) *
        100;

      const normalized =
        numericValue * multiplier;

      const minValue =
        numericValue * 0.88;

      const maxValue =
        numericValue * 1.15;

      const range =
        maxValue - minValue || 1;

      const y =
        34 -
        ((normalized - minValue) /
          range) *
          27;

      return `${x},${Math.max(
        4,
        Math.min(34, y)
      )}`;
    })
    .join(" ");

  return (
    <div
      className={`market-mini-chart market-mini-chart-${variant}`}
    >
      <svg
        viewBox="0 0 100 40"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <polyline
          points={points}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

// =========================================================
// MARKET COMPONENT
// =========================================================

const Market = () => {
  // =======================================================
  // NAVIGATION
  // =======================================================

  const navigate = useNavigate();

  // =======================================================
  // MARKET STATE
  // =======================================================

  const [
    marketData,
    setMarketData,
  ] = useState(null);

  const [
    blockNumber,
    setBlockNumber,
  ] = useState(0);

  const [
    circulatingSupply,
    setCirculatingSupply,
  ] = useState(0);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const [
    rpcConnected,
    setRpcConnected,
  ] = useState(false);

  const [
    lastUpdated,
    setLastUpdated,
  ] = useState(null);

  // =======================================================
  // FETCH GOLD DATA
  // =======================================================

  const fetchGoldData =
    useCallback(async () => {
      const response =
        await fetch(GOLD_API);

      if (!response.ok) {
        throw new Error(
          `Gold API request failed: ${response.status}`
        );
      }

      const data =
        await response.json();

      console.log(
        "BAIT Gold API:",
        data
      );

      setMarketData(data);

      return data;
    }, []);

  // =======================================================
  // FETCH BLOCKCHAIN DATA
  // =======================================================

  const fetchBlockchainData =
    useCallback(async () => {
      try {
        // ---------------------------------------------------
        // GET CHAIN ID
        // ---------------------------------------------------

        const chainIdHex =
          await rpcCall(
            "eth_chainId"
          );

        const chainId =
          hexToNumber(
            chainIdHex
          );

        console.log(
          "GoldChain ID:",
          chainId
        );

        // ---------------------------------------------------
        // VERIFY NETWORK
        // ---------------------------------------------------

        if (
          chainId !== CHAIN_ID
        ) {
          throw new Error(
            `Wrong network. Expected ${CHAIN_ID}, received ${chainId}`
          );
        }

        // ---------------------------------------------------
        // GET CURRENT BLOCK
        // ---------------------------------------------------

        const blockHex =
          await rpcCall(
            "eth_blockNumber"
          );

        const currentBlock =
          hexToNumber(
            blockHex
          );

        console.log(
          "GoldChain block:",
          currentBlock
        );

        setBlockNumber(
          currentBlock
        );

        // ---------------------------------------------------
        // CALCULATE CIRCULATING SUPPLY
        // ---------------------------------------------------

        const estimatedSupply =
          GENESIS_SUPPLY +
          currentBlock *
            BLOCK_REWARD;

        setCirculatingSupply(
          estimatedSupply
        );

        setRpcConnected(
          true
        );

        return {
          chainId,
          currentBlock,
          estimatedSupply,
        };
      } catch (err) {
        console.error(
          "GoldChain RPC error:",
          err
        );

        setRpcConnected(
          false
        );

        throw err;
      }
    }, []);

  // =======================================================
  // FETCH MARKET DATA
  // =======================================================

  const fetchMarketData =
    useCallback(async () => {
      try {
        setLoading(true);
        setError("");

        await Promise.all([
          fetchGoldData(),
          fetchBlockchainData(),
        ]);

        setLastUpdated(
          new Date()
        );
      } catch (err) {
        console.error(
          "Market data error:",
          err
        );

        setError(
          err?.message ||
            "Unable to load current market data."
        );
      } finally {
        setLoading(false);
      }
    }, [
      fetchGoldData,
      fetchBlockchainData,
    ]);

  // =======================================================
  // INITIAL LOAD + AUTO REFRESH
  // =======================================================

  useEffect(() => {
    fetchMarketData();

    const interval =
      setInterval(
        fetchMarketData,
        60000
      );

    return () => {
      clearInterval(
        interval
      );
    };
  }, [
    fetchMarketData,
  ]);

  // =======================================================
  // FORMAT USD
  // =======================================================

  const formatUSD = (
    value
  ) => {
    if (
      value === undefined ||
      value === null ||
      !Number.isFinite(
        Number(value)
      )
    ) {
      return "$0.00";
    }

    return new Intl.NumberFormat(
      "en-US",
      {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    ).format(
      Number(value)
    );
  };

  // =======================================================
  // FORMAT NUMBER
  // =======================================================

  const formatNumber = (
    value
  ) => {
    if (
      value === undefined ||
      value === null ||
      !Number.isFinite(
        Number(value)
      )
    ) {
      return "0";
    }

    return new Intl.NumberFormat(
      "en-US"
    ).format(
      Number(value)
    );
  };

  // =======================================================
  // FORMAT PRICE
  // =======================================================

  const formatPrice = (
    value
  ) => {
    if (
      value === undefined ||
      value === null ||
      !Number.isFinite(
        Number(value)
      )
    ) {
      return "$0.00";
    }

    return `$${Number(
      value
    ).toLocaleString(
      "en-US",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }
    )}`;
  };

  // =======================================================
  // MARKET VALUES
  // =======================================================

  const baitPrice =
    Number(
      marketData?.bait?.price_usd
    ) || 0;

  const goldPerOz =
    Number(
      marketData?.gold?.usd_per_oz
    ) || 0;

  const goldPerGram =
    Number(
      marketData?.gold?.usd_per_gram
    ) || 0;

  const goldBacking =
    Number(
      marketData?.bait
        ?.gold_backing_grams
    ) || 0;

  // =======================================================
  // MARKET CAP
  // =======================================================

  const marketCap =
    baitPrice *
    circulatingSupply;

  // =======================================================
  // TOTAL GOLD BACKING
  // =======================================================

  const totalGoldBacking =
    goldBacking *
    circulatingSupply;

  // =======================================================
  // GOLD RESERVE VALUE
  // =======================================================

  const goldBackingValue =
    totalGoldBacking *
    goldPerGram;

  // =======================================================
  // SHARED PAGE STYLE
  // =======================================================

  const pageStyle = {
    width: `${PAGE_WIDTH}px`,
    height: `${PAGE_HEIGHT}px`,
    minWidth: `${PAGE_WIDTH}px`,
    maxWidth: `${PAGE_WIDTH}px`,
    minHeight: `${PAGE_HEIGHT}px`,
    maxHeight: `${PAGE_HEIGHT}px`,
    boxSizing: "border-box",
  };

  // =======================================================
  // LOADING SCREEN
  // =======================================================

  if (
    loading &&
    !marketData
  ) {
    return (
      <div
        className="market-page"
        style={pageStyle}
      >

        <div className="market-loading">

          <RefreshCw
            size={28}
            className="market-spinner"
          />

          <p>
            Loading BAIT market data...
          </p>

        </div>

      </div>
    );
  }

  // =======================================================
  // MAIN RETURN
  // =======================================================

  return (
    <div
      className="market-page"
      style={pageStyle}
    >

      {/* =================================================
          MARKET HEADER
      ================================================= */}

      <div className="market-header">

        {/* BACK BUTTON */}
        <button
          type="button"
          className="market-back-btn"
          onClick={() => navigate("/yourwallet")}
          title="Back to Wallet"
        >
          <ArrowLeft size={18} />
        </button>

        {/* MARKET TITLE */}
        <div className="market-title">

          <div>
            <h1>
              BAIT Market
            </h1>

            <p>
              Gold-backed digital asset market
            </p>
          </div>

        </div>

      </div>

      {/* =================================================
          ERROR
      ================================================= */}

      {error && (
        <div className="market-error">

          <span>
            {error}
          </span>

          <button
            type="button"
            onClick={
              fetchMarketData
            }
          >
            Retry
          </button>

        </div>
      )}

      {/* =================================================
          MAIN BAIT CARD
      ================================================= */}

      <div className="bait-market-card">

        <div className="bait-market-top">

          <div className="bait-asset">

            <img
              src={BaitLogo}
              alt="BAIT"
              className="bait-market-logo"
            />

            <div>
              <h2>
                BAIT
              </h2>

              <span>
                GoldChain Native Asset
              </span>
            </div>

          </div>

          <div className="asset-status">

            <div className="live-price-label">

              <span className="status-dot" />

              <span>
                Live Price
              </span>

            </div>

            <button
              className="market-refresh-btn"
              onClick={
                fetchMarketData
              }
              disabled={loading}
              type="button"
              title="Refresh market data"
              aria-label="Refresh market data"
            >
              <RefreshCw
                size={17}
                className={
                  loading
                    ? "market-spinner"
                    : ""
                }
              />
            </button>

          </div>

        </div>

        <div className="bait-price-section">

          <span className="price-label">
            BAIT Price
          </span>

          <div className="bait-price">
            {formatUSD(
              baitPrice
            )}
          </div>

          <div className="price-backing">

            <Gem size={15} />

            <span>
              Backed by{" "}
              {goldBacking} g gold
            </span>

          </div>

        </div>

      </div>

      {/* =================================================
          MARKET STATISTICS
      ================================================= */}

      <div className="market-stats-grid">

        {/* MARKET CAP */}
        <div className="market-stat-card">

          <div className="market-stat-top">

            <div className="market-stat-icon">
              <TrendingUp size={14} />
            </div>

          </div>

          <div className="market-stat-content">

            <span>
              Market Cap
            </span>

            <strong>
              {formatUSD(marketCap)}
            </strong>

            <small>
              BAIT × circulating supply
            </small>

          </div>

        </div>


        {/* CIRCULATING SUPPLY */}
        <div className="market-stat-card">

          <div className="market-stat-top">

            <div className="market-stat-icon">
              <Coins size={14} />
            </div>

          </div>

          <div className="market-stat-content">

            <span>
              Circulating Supply
            </span>

            <strong>
              {formatNumber(circulatingSupply)} BAIT
            </strong>

            <small>
              Blockchain calculated
            </small>

          </div>

        </div>


        {/* CURRENT BLOCK */}
        <div className="market-stat-card">

          <div className="market-stat-top">

            <div className="market-stat-icon">
              <Blocks size={14} />
            </div>

          </div>

          <div className="market-stat-content">

            <span>
              Current Block
            </span>

            <strong>
              #{formatNumber(blockNumber)}
            </strong>

            <small>
              GoldChain network
            </small>

          </div>

        </div>


        {/* GOLD PRICE */}
        <div className="market-stat-card">

          <div className="market-stat-top">

            <div className="market-stat-icon">
              <Gem size={14} />
            </div>

          </div>

          <div className="market-stat-content">
 <span>
        Gold Price
      </span>
          

            <strong>
              {formatPrice(goldPerGram)}

              <em>
                / g
              </em>
            </strong>

            <small>
              {formatUSD(goldPerOz)} / oz
            </small>

          </div>

        </div>

      </div>

      {/* =================================================
          GOLD BACKING
      ================================================= */}

      <div className="gold-backing-card">

        <div className="gold-backing-header">

          <div className="gold-backing-icon">
            <Gem size={22} />
          </div>

          <div>

            <h3>
              Gold Backing
            </h3>

            <p>
              BAIT reference value
              based on current gold
              price
            </p>

          </div>

        </div>

        <div className="gold-backing-grid">

          <div className="gold-info">

            <span>
              Gold Price / Ounce
            </span>

            <strong>
              {formatUSD(
                goldPerOz
              )}
            </strong>

          </div>

          <div className="gold-info">

            <span>
              Gold Price / Gram
            </span>

            <strong>
              {formatUSD(
                goldPerGram
              )}
            </strong>

          </div>

          <div className="gold-info">

            <span>
              BAIT Gold Backing
            </span>

            <strong>
              {goldBacking} g
            </strong>

          </div>

          <div className="gold-info">

            <span>
              Calculated BAIT Value
            </span>

            <strong>
              {formatUSD(
                goldPerGram *
                  goldBacking
              )}
            </strong>

          </div>

        </div>

      </div>

      {/* =================================================
          RESERVE
      ================================================= */}

      <div className="reserve-card">

        <div>

          <span>
            Estimated Gold Reserve
          </span>

          <strong>
            {formatNumber(
              totalGoldBacking
            )}{" "}
            grams
          </strong>

        </div>

        <div>

          <span>
            Gold Reserve Value
          </span>

          <strong>
            {formatUSD(
              goldBackingValue
            )}
          </strong>

        </div>

      </div>

      {/* =================================================
          NETWORK STATUS
      ================================================= */}

      <div className="market-footer">

        <div className="api-status">

          <span
            className={
              rpcConnected
                ? "status-dot"
                : "status-dot offline"
            }
          />

          <Server size={14} />

          <span>
            {rpcConnected
              ? "GoldChain RPC Connected"
              : "GoldChain RPC Offline"}
          </span>

        </div>

        <div className="api-status">

          <span className="status-dot" />

          <span>
            Gold Reference API
            Connected
          </span>

        </div>

        {lastUpdated && (
          <span className="updated-time">
            Updated{" "}
            {lastUpdated.toLocaleTimeString()}
          </span>
        )}

      </div>

    </div>
  );
};

export default Market;